import { useCallback } from 'react';
import { useGetAiworkersPresignedUrlListKeyMutation, useGetAiworkersPresignedUrlByKeyMutation } from '@circuitry-ai/doc-data';

export interface ResolveUxgenieHtmlParams {
  aiworker_id: string;
  aiworker_usage_id: string;
  fileName?: string;
}

export interface ResolveUxgenieHtmlUrlParams {
  aiworker_id: string;
  aiworker_usage_id: string;
  source_id: string;
  /** File extension, e.g. `html` — no leading dot. */
  extension: string;
}

/**
 * Resolves a UXGenie HTML template that was saved to S3 (WAF blocks large inline
 * HTML in the save request) back into its raw HTML text: list objects under the
 * usage's S3 prefix, map to the saved file name, fetch a view presigned URL, then
 * fetch its text content.
 *
 * Kept for templates saved before `source_id` was stored on `action_output` —
 * prefer `resolveHtmlUrl` for anything saved afterwards, which skips the list step.
 */
export function useUxgenieTemplateHtmlResolver() {
  const [listKeys] = useGetAiworkersPresignedUrlListKeyMutation();
  const [viewByKey] = useGetAiworkersPresignedUrlByKeyMutation();

  // useCallback keeps these stable across renders (the underlying mutation triggers
  // already are) so consumers can safely list them in effect dependency arrays
  // without re-triggering a fetch on every render.
  const resolveHtml = useCallback(async ({ aiworker_id, aiworker_usage_id, fileName }: ResolveUxgenieHtmlParams): Promise<string> => {
    const base_prefix = `aiworkers_usage/uxgenie/${aiworker_id}/${aiworker_usage_id}/`;
    const listResult = await listKeys({ base_prefix }).unwrap();
    const keys = listResult?.keys ?? [];
    const match = (fileName ? keys.find((k: any) => k.file_name === fileName) : undefined) ?? keys[0];
    if (!match) throw new Error('Saved template file not found in storage');

    const viewResult = await viewByKey({ s3_key: match.key }).unwrap();
    if (!viewResult?.presigned_url) throw new Error('Failed to resolve template file URL');

    const response = await fetch(viewResult.presigned_url);
    if (!response.ok) throw new Error('Failed to fetch saved template content');
    return await response.text();
  }, [listKeys, viewByKey]);

  /**
   * Resolves the S3 key directly from `{ aiworker_id, aiworker_usage_id, source_id }`
   * (stored on `action_output` at save time) and returns a presigned view URL —
   * no list-key round trip needed.
   */
  const resolveHtmlUrl = useCallback(async ({ aiworker_id, aiworker_usage_id, source_id, extension }: ResolveUxgenieHtmlUrlParams): Promise<string> => {
    const s3_key = `aiworkers_usage/uxgenie/${aiworker_id}/${aiworker_usage_id}/${source_id}.${extension}`;
    const viewResult = await viewByKey({ s3_key }).unwrap();
    if (!viewResult?.presigned_url) throw new Error('Failed to resolve template file URL');
    return viewResult.presigned_url;
  }, [viewByKey]);

  return { resolveHtml, resolveHtmlUrl };
}
